// =============================================================================
// The capture station's "Last saved" line (F11 UF-01).
//
// A station save used to confirm with a full-screen card that blocked the
// tablet for the round trip and then 1.2 s more, about 300 times a shift. The
// confirmation now stays where the supervisor is looking: a small badge while
// it saves, then this line — who, how many, when, and her day as the SERVER
// worked it out (save_packout_drops returns `totals[].day_units`).
//
// Pure, so the wording is pinned without rendering the screen.
// =============================================================================

import { fmtQty } from '@/lib/numberFormat'

/** One key the batch touched, with the day figure the SERVER worked out for it
 *  (save_packout_drops step 14 — `totals[].day_units`, mig 20261024570000). */
export interface PackoutDayTotal {
  worker_number: string
  work_date:     string
  unit_type:     string
  line_ref:      string | null
  day_units:     number
}

/** What a station save resolved to. `totals` is the server's answer and is
 *  only ever non-empty for a save that was SENT — a queued save has no server
 *  figure yet, and the screen must not invent one. */
export interface PackoutSaveResult {
  status: 'sent' | 'queued'
  totals: PackoutDayTotal[]
}

/** Read the RPC's `totals` defensively: a row that does not carry a finite
 *  day figure is dropped rather than shown as 0 — "her day: 0" for a save
 *  that just landed would be a false claim about pay. */
export function packoutTotalsOf(data: unknown): PackoutDayTotal[] {
  const raw = (data as { totals?: unknown } | null)?.totals
  if (!Array.isArray(raw)) return []
  return raw.flatMap((t): PackoutDayTotal[] => {
    const r = t as Record<string, unknown>
    const day = r.day_units == null ? NaN : Number(r.day_units)
    if (typeof r.worker_number !== 'string' || typeof r.unit_type !== 'string' || !Number.isFinite(day)) return []
    return [{
      worker_number: r.worker_number,
      work_date:     String(r.work_date ?? ''),
      unit_type:     r.unit_type,
      line_ref:      typeof r.line_ref === 'string' ? r.line_ref : null,
      day_units:     day,
    }]
  })
}

export interface LastSaved {
  name:     string
  units:    number
  unit:     string
  lineRef:  string | null
  savedAt:  string
  status:   PackoutSaveResult['status']
  /** The server's figure for her, this day, this unit, this line — null when
   *  the save was queued (no server figure yet) or the answer did not carry
   *  her key. Never a client-side sum: the day list is capped and partial. */
  dayUnits: number | null
}

/** Pick HER key out of the server's totals. The key is the one the drop was
 *  sent with: worker, unit, line (an unlined drop rolls up under no line). */
export function serverDayUnits(
  totals: PackoutDayTotal[], workerNumber: string, unit: string, lineRef: string | null,
): number | null {
  const hit = totals.find(t =>
    t.worker_number === workerNumber && t.unit_type === unit && (t.line_ref ?? '') === (lineRef ?? ''))
  return hit ? hit.day_units : null
}

/** "Nomsa Dlamini · 10 pun125 · 10:41 · her day on Line 2: 172 pun125".
 *  A queued save says it is on this tablet and not sent yet — and claims no
 *  day figure, because the server has not worked one out. */
export function lastSavedText(r: LastSaved, fmtTime: (iso: string) => string): string {
  const unit = r.unit.toLowerCase()
  const head = `${r.name} · ${fmtQty(r.units)} ${unit} · ${fmtTime(r.savedAt)}`
  if (r.status === 'queued') return `${head} · on this tablet, not sent yet — it will sync`
  if (r.dayUnits == null) return head
  const where = r.lineRef ? ` on ${r.lineRef}` : ''
  return `${head} · her day${where}: ${fmtQty(r.dayUnits)} ${unit}`
}

/**
 * The refusal dialog for a station save. While the packer it was for is still
 * the one on screen, the server's sentence is enough. Once the screen has moved
 * on — a card scanned mid-save is the next packer, by design — the dialog opens
 * over HER, and most refusal sentences name nobody. So it says who and what was
 * not saved first; otherwise the supervisor reads it as the next packer's
 * refusal, and the first packer's units are never captured again.
 */
export function refusedSaveText(
  sentence: string,
  saved: { name: string; units: number; unit: string },
  stillOnScreen: boolean,
): string {
  if (stillOnScreen) return sentence
  return `Not saved for ${saved.name} — ${fmtQty(saved.units)} ${saved.unit.toLowerCase()}: ${sentence}`
}
